import React, { useState } from "react";
import moment from "moment";
import { Form, Input, Select, InputNumber, Button, Modal } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/es/form";
import styled from "@emotion/styled";
import { Employer } from "../employer-list/list";
import { AdminSelect } from "components/admin-select";
import { District } from "components/district";
import { changeArrayToStringInObject } from "utils";
import { employerStatus } from "utils/common";

interface SearchPanelProps extends Employer {
  onUpdate: (value: Partial<Employer>) => void;
  loading: boolean;
  reload: () => void;
}

export const CompanyEmployerDetails = ({
  onUpdate,
  reload,
  loading,
  ...data
}: SearchPanelProps) => {
  const formatData = {
    uuid: data.uuid,
    companyName: data.companyName,
    contactNumber: data.contactNumber,
    district: data?.district?.split(",") || [],
    address: data.address,
    email: data.email,
    status: data.status,
    personInCharge: data.personInCharge?.id,
  };

  const editBasicInfoFormRef = React.createRef<FormInstance>();
  const [editBasicInfoForm] = Form.useForm();
  const [showEditBasicInfoModal, setShowEditBasicInfoModal] = useState(false);

  const editContactFormRef = React.createRef<FormInstance>();
  const [editContactForm] = Form.useForm();
  const [showEditContactModal, setShowEditContactModal] = useState(false);

  const editAdminInfoFormRef = React.createRef<FormInstance>();
  const [editAdminInfoForm] = Form.useForm();
  const [showEditAdminInfoModal, setShowEditAdminInfoModal] = useState(false);

  const onEditFormClick = (type: string) => {
    switch (type) {
      case "basic":
        setShowEditBasicInfoModal(!showEditBasicInfoModal);
        break;
      case "contact":
        editContactForm.setFieldsValue(formatData);
        setShowEditContactModal(!showEditContactModal);
        break;
      case "adminInfo":
        editAdminInfoForm.setFieldsValue(formatData);
        setShowEditAdminInfoModal(!showEditAdminInfoModal);
        break;
    }
  };

  const onEditModalSubmit = async (values: Partial<Employer>) => {
    await onUpdate(changeArrayToStringInObject(values));
    reload();

    if (showEditBasicInfoModal) {
      setShowEditBasicInfoModal(!showEditBasicInfoModal);
    }

    if (showEditContactModal) {
      setShowEditContactModal(!showEditContactModal);
    }

    if (showEditAdminInfoModal) {
      setShowEditAdminInfoModal(!showEditAdminInfoModal);
    }
  };

  return (
    <>
      <SectionContainer>
        <SectionTitle>employer information</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("basic")} />
        </EditButton>
        <DataContainer>
          <DataTitle>company name:</DataTitle>
          <DataContent>{data?.companyName}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>Person in charge:</DataTitle>
          <DataContent>{data?.contactPerson}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>rank of person in charge:</DataTitle>
          <DataContent>{data?.contactPersonPosition}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>community voucher number:</DataTitle>
          <DataContent>{data?.code}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>community voucher percentage:</DataTitle>
          <DataContent>{data?.codePercentage && `${data?.codePercentage}%`}</DataContent>
        </DataContainer>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>contact information</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("contact")} />
        </EditButton>
        <DataContainer>
          <DataTitle>contact number:</DataTitle>
          <DataContent>{data?.contactNumber}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>email:</DataTitle>
          <DataContent>{data?.email}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>area:</DataTitle>
          <DataContent>{data?.district}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>address:</DataTitle>
          <DataContent>{data?.address}</DataContent>
        </DataContainer>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>UniCare remark</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("adminInfo")} />
        </EditButton>
        <DataContainer>
          <DataTitle>person in charge:</DataTitle>
          <DataContent>{data?.personInCharge?.username}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>status:</DataTitle>
          <DataContent>{employerStatus(data?.status)}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>remark:</DataTitle>
          <DataContent>{data?.remark}</DataContent>
        </DataContainer>
      </SectionContainer>

      <Modal
        title={"edit employer information"}
        visible={showEditBasicInfoModal}
        footer={null}
        onCancel={() => onEditFormClick("basic")}
      >
        <Form
          form={editBasicInfoForm}
          ref={editBasicInfoFormRef}
          layout="vertical"
          initialValues={data}
          onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="uuid" hidden>
            <Input placeholder="uuid" />
          </Form.Item>

          <Form.Item
            name="companyName"
            label="company name"
            rules={[{ required: true, message: "please enter your company name" }]}
          >
            <Input placeholder="company name" />
          </Form.Item>

          <Form.Item
            name="contactPerson"
            label="name of person in charge"
            rules={[{ required: true, message: "please enter the name of the person in charge" }]}
          >
            <Input placeholder="name of person in charge" />
          </Form.Item>

          <Form.Item name="contactPersonPosition" label="rank of person in charge">
            <Input placeholder="rank of person in charge" />
          </Form.Item>

          <Form.Item name="code" label="community voucher number">
            <Input placeholder="community voucher number" />
          </Form.Item>

          <Form.Item name="codePercentage" label="community voucher percentage">
            <InputNumber placeholder="community voucher percentage" style={{ width: "100%" }} />
          </Form.Item>

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("basic")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form>
      </Modal>

      <Modal
        title={"edit your contact information"}
        visible={showEditContactModal}
        footer={null}
        onCancel={() => onEditFormClick("contact")}
      >
        <Form
          form={editContactForm}
          ref={editContactFormRef}
          layout="vertical"
          initialValues={data}
          onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="uuid" hidden>
            <Input placeholder="uuid" />
          </Form.Item>

          <Form.Item
            name="contactNumber"
            label="contact number"
            rules={[{ required: true, message: "please enter your contact number" }]}
          >
            <Input placeholder="contact number" />
          </Form.Item>

          <Form.Item
            name="email"
            label="email"
            rules={[{ required: true, message: "please enter your email address" }]}
          >
            <Input placeholder="email" />
          </Form.Item>

          <Form.Item
            name="district"
            label="regions served"
            rules={[{ required: true, message: "please enter the service regions" }]}
          >
            <District />
          </Form.Item>

          <Form.Item
            name="address"
            label="full service address"
            rules={[{ required: true, message: "please enter the full service address" }]}
          >
            <Input placeholder="full service address" />
          </Form.Item>

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("contact")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </Modal>

      <Modal
        title={"edit UniCare remark"}
        visible={showEditAdminInfoModal}
        footer={null}
        onCancel={() => onEditFormClick("adminInfo")}
      >
        <Form
          form={editAdminInfoForm}
          ref={editAdminInfoFormRef}
          layout="vertical"
          initialValues={data}
          onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="uuid" hidden>
            <Input placeholder="uuid" />
          </Form.Item>

          <Form.Item name="personInCharge" label="responsible for employees">
            <AdminSelect placeholder="responsible for employees" />
          </Form.Item>

          <Form.Item name="status" label="status">
            <Select placeholder="status">
              <Select.Option value={0}>enable</Select.Option>
              <Select.Option value={1}>troublemakers</Select.Option>
              <Select.Option value={2}>deactivated</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="remark" label="remark">
            <Input.TextArea
              placeholder="remark"
              autoSize={{ minRows: 2, maxRows: 6 }}
            />
          </Form.Item>

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("adminInfo")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </Modal>
    </>
  );
};

const SectionContainer = styled.div`
  background-color: #ffffff;
  width: 100%;
  padding: 15px;
  margin-bottom: 10px;
  position: relative;
`;

const SectionTitle = styled.div`
  color: #3c3e42;
  font-size: 20px;
  font-weight: bold;
  padding-bottom: 6px;
`;

const EditButton = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
`;

const DataContainer = styled.div`
  // padding-top: 2px;
`;

const DataTitle = styled.div`
  display: inline-block;
  font-size: 14px;
  color: #8d939e;
  padding-right: 5px;
`;

const DataContent = styled.div`
  display: inline-block;
  font-size: 14px;
  color: #3c3e42;
  font-weight: 500;
`;
